import React, { useState, useEffect, useContext } from "react";
import "./FinancialPlan.css";
import Coinssvg from "../../../../images/projects/ProjectFinance/Coinssvg.svg";
import House from "../../../../images/projects/ProjectFinance/House.svg";
import People from "../../../../images/projects/ProjectFinance/People.svg";
import Sun from "../../../../images/projects/ProjectFinance/Sun.svg";
import Print from "../../../../images/projects/ProjectFinance/Print.png";
import Sigma from "../../../../images/projects/ProjectFinance/Sigma.svg";
import AddUser from "../../../../images/projects/ProjectFinance/AddUser.svg";
import WorkTab from "./WorkTab/WorkTab";
import BimTab from "./BimTab/BimTab";
import QualityTab from "./QualityTab/QualityTab";
import LeadTab from "./LeadTab/LeadTab";
import ProjectTab from "./ProjectTab/ProjectTab";
import ContractSectionsTab from "./ContractSectionsTab/ContractSectionsTab";
import VersionHistoryDrawer from "./VersionHistoryDrawer/VersionHistoryDrawer";
import ApprovalModal from "./ApprovalModal/ApprovalModal";
import SelectSpecialistModal from "./SelectSpecialistModal";
import {
  FinancialPlanStateProvider,
  useFinancialPlanState,
} from "./FinancialPlanContext";
import Preloader from "../../../Preloader/Preloader";
import {
  getFinancialPlanApprovals,
  getFinancialPlanApprovalStatuses,
  requestFinancialPlanApproval,
  cancelFinancialPlanApprovalRequest,
  getProjectById,
} from "../../../../api/projectApi";
import { UserContext } from "../../../../contexts/CurrentUserContext";

// Основной компонент финансового плана
const FinancialPlanComponent = ({ projectId }) => {
  const { userData } = useContext(UserContext);
  const [activeSubTab, setActiveSubTab] = useState("working-design");
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showSelectSpecialistModal, setShowSelectSpecialistModal] =
    useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Состояния для согласования финансового плана
  const [projectData, setProjectData] = useState(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isCancellingApproval, setIsCancellingApproval] = useState(false);
  const [financialPlanApprovals, setFinancialPlanApprovals] = useState([]);
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [isEditable, setIsEditable] = useState(true);

  // Получаем функции из контекста
  const {
    financialPlanData,
    loadFinancialPlanData,
    saveFinancialPlan,
    isLoading,
    error,
    selectedSpecialists,
    addSpecialist,
    removeSpecialist,
  } = useFinancialPlanState();

  const subTabs = [
    { id: "working-design", label: "Рабочее проектирование" },
    { id: "bim-coordination", label: "BIM координирование" },
    { id: "quality-control", label: "Контроль качества" },
    { id: "project-delivery", label: "Ведение проекта" },
    { id: "project-management", label: "Управление проектом" },
    { id: "contract-sections", label: "Разделы на договоре" },
  ];

  // Загружаем данные при монтировании компонента
  useEffect(() => {
    if (projectId) {
      loadFinancialPlanData(projectId);
    }
  }, [projectId, loadFinancialPlanData]);

  // Загрузка данных проекта для согласования
  useEffect(() => {
    const loadProjectData = async () => {
      if (!projectId) return;

      try {
        const data = await getProjectById(projectId);
        setProjectData(data);
      } catch (error) {
        console.error("Ошибка при загрузке данных проекта:", error);
      }
    };

    loadProjectData();
  }, [projectId]);

  // Загрузка списка согласований финансового плана для сравнения версий
  useEffect(() => {
    const loadFinancialPlanApprovals = async () => {
      if (!projectId) return;

      try {
        const approvals = await getFinancialPlanApprovals(projectId);
        setFinancialPlanApprovals(approvals || []);
      } catch (error) {
        console.error("Ошибка при загрузке списка согласований:", error);
        setFinancialPlanApprovals([]);
      }
    };

    loadFinancialPlanApprovals();
  }, [projectId]);

  // Загрузка статусов согласования финансового плана
  useEffect(() => {
    const loadApprovalStatuses = async () => {
      if (!projectId) return;

      try {
        const statuses = await getFinancialPlanApprovalStatuses(
          parseInt(projectId)
        );
        setApprovalStatuses(statuses || []);
      } catch (error) {
        console.error("Ошибка при загрузке статусов согласования:", error);
        // Не критично, продолжаем работу
        setApprovalStatuses([]);
      }
    };

    loadApprovalStatuses();
  }, [projectId]);

  // Обновление флага редактируемости на основе поля editable из API
  useEffect(() => {
    if (!financialPlanData?.rawApiData) return;

    // Используем значение editable из API
    // Если editable === false, значит финансовый план на согласовании
    setIsEditable(financialPlanData.rawApiData.editable !== false);
  }, [financialPlanData]);

  // Находим активное согласование (только то, которое еще не завершено)
  // Используем approvalStatuses для определения активного согласования (как в Estimate.js)
  // Выбираем самое новое активное согласование (с наибольшим id или версией)
  const activeApproval = Array.isArray(approvalStatuses)
    ? approvalStatuses
        .filter((approval) => {
          // Проверяем общий статус согласования - исключаем завершенные и отмененные
          const status = approval.status?.toLowerCase() || "";
          if (
            status === "согласован" ||
            status === "approved" ||
            status === "отклонен" ||
            status === "rejected" ||
            status === "отменен инициатором" ||
            status === "не согласован"
          ) {
            return false; // Согласование уже завершено или отменено
          }

          // Если статус "на рассмотрении" - это активное согласование
          if (status === "на рассмотрении" || status === "pending") {
            return true;
          }

          // Если есть шаги, проверяем их статусы
          if (approval.steps && approval.steps.length > 0) {
            // Проверяем, есть ли хотя бы один шаг, который еще не завершен
            return approval.steps.some((step) => {
              const stepStatus = step.status?.toLowerCase() || "";
              return (
                stepStatus === "pending" ||
                stepStatus === "на рассмотрении" ||
                stepStatus === "" ||
                step.status === null ||
                step.status === undefined
              );
            });
          }

          // Если шагов нет, но статус не завершен, считаем активным
          return !approval.status || status === "";
        })
        .sort((a, b) => {
          // Сортируем по версии (если есть) или по id, чтобы выбрать самое новое
          if (a.version !== undefined && b.version !== undefined) {
            return b.version - a.version;
          }
          return (b.id || 0) - (a.id || 0);
        })[0] || null
    : null;

  // Проверяем, является ли текущий пользователь инициатором согласования
  const isInitiator =
    activeApproval && userData && activeApproval.initiator_id === userData.id;

  // Показываем кнопку отмены только если:
  // 1. Есть активное согласование
  // 2. Пользователь является инициатором
  // 3. Финансовый план не редактируемый (на согласовании)
  // 4. Есть шаги или статус "На рассмотрении" (как в Estimate.js)
  const hasActiveApproval =
    activeApproval !== undefined && activeApproval !== null;
  const hasSteps = activeApproval?.steps && activeApproval.steps.length > 0;
  const showCancelButton =
    hasActiveApproval &&
    (hasSteps ||
      activeApproval?.status === "На рассмотрении" ||
      activeApproval?.status === "на рассмотрении" ||
      activeApproval?.status === "pending") &&
    isInitiator &&
    !isEditable;

  // Отладочная информация
  console.log("Отладка согласования финансового плана:", {
    approvalStatuses: approvalStatuses?.map((a) => ({
      id: a.id,
      status: a.status,
      initiator_id: a.initiator_id,
      steps: a.steps?.map((s) => ({ id: s.id, status: s.status })),
    })),
    activeApproval: activeApproval
      ? {
          id: activeApproval.id,
          status: activeApproval.status,
          initiator_id: activeApproval.initiator_id,
          steps: activeApproval.steps,
        }
      : null,
    isInitiator,
    hasActiveApproval,
    hasSteps,
    showCancelButton,
    isEditable,
    userData: userData?.id,
  });

  // Функции для обработки действий
  const handleApprovalSubmit = async (selectedApprovers) => {
    if (!projectId || !financialPlanData?.headerData) {
      alert("Ошибка: отсутствуют данные проекта или финансового плана");
      return;
    }

    setIsSubmittingApproval(true);
    try {
      const totalPrice = financialPlanData.headerData?.contract_value || 0;

      console.log("Отправка финансового плана на согласование:", {
        projectId,
        approverIds: selectedApprovers,
        totalPrice,
      });

      const result = await requestFinancialPlanApproval(
        projectId,
        selectedApprovers,
        totalPrice
      );

      console.log("Результат отправки на согласование:", result);

      // Показываем успешное сообщение
      setSuccessMessage("Финансовый план успешно отправлен на согласование!");
      setShowSuccessModal(true);

      // Закрываем модальное окно
      setShowApprovalModal(false);

      // Перезагружаем данные финансового плана, чтобы обновился флаг editable
      await loadFinancialPlanData(projectId);

      // Перезагружаем список согласований
      const approvals = await getFinancialPlanApprovals(projectId);
      setFinancialPlanApprovals(approvals || []);

      // Перезагружаем статусы согласования
      const statuses = await getFinancialPlanApprovalStatuses(
        parseInt(projectId)
      );
      setApprovalStatuses(statuses || []);

      // Перезагружаем страницу через небольшую задержку, чтобы обновились все данные
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Ошибка при отправке на согласование:", error);
      setErrorMessage(`Ошибка при отправке на согласование: ${error.message}`);
      setShowErrorModal(true);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  // Функции для отображения статусов согласования
  const getStatusIcon = (status) => {
    if (!status)
      return (
        <span className="financial-plan-approval-status-icon pending">⏳</span>
      );
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return (
        <span className="financial-plan-approval-status-icon approved">✓</span>
      );
    }
    if (statusLower === "на рассмотрении" || statusLower === "pending") {
      return (
        <span className="financial-plan-approval-status-icon pending">⏳</span>
      );
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return (
        <span className="financial-plan-approval-status-icon rejected">✕</span>
      );
    }
    return (
      <span className="financial-plan-approval-status-icon pending">⏳</span>
    );
  };

  const getStatusText = (status) => {
    if (!status) return "На рассмотрении";
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return "Согласован";
    }
    if (statusLower === "на рассмотрении" || statusLower === "pending") {
      return "На рассмотрении";
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return "Отклонен";
    }
    return "На рассмотрении";
  };

  // Обработчик отмены согласования
  const handleCancelApproval = async () => {
    if (!projectId) return;

    if (
      !window.confirm(
        "Вы уверены, что хотите отозвать отправку на согласование?"
      )
    ) {
      return;
    }

    try {
      setIsCancellingApproval(true);
      await cancelFinancialPlanApprovalRequest(parseInt(projectId));

      // Перезагружаем данные финансового плана, чтобы обновился флаг editable
      await loadFinancialPlanData(projectId);

      // Перезагружаем список согласований
      const approvals = await getFinancialPlanApprovals(projectId);
      setFinancialPlanApprovals(approvals || []);

      // Перезагружаем статусы согласования
      const statuses = await getFinancialPlanApprovalStatuses(
        parseInt(projectId)
      );
      setApprovalStatuses(statuses || []);

      // Показываем успешное сообщение
      setSuccessMessage("Отправка на согласование успешно отозвана!");
      setShowSuccessModal(true);

      // Перезагружаем страницу через небольшую задержку, чтобы пользователь увидел сообщение
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Ошибка при отмене согласования:", error);
      setErrorMessage(`Ошибка при отмене согласования: ${error.message}`);
      setShowErrorModal(true);
    } finally {
      setIsCancellingApproval(false);
    }
  };

  const handleSave = async () => {
    try {
      const result = await saveFinancialPlan(projectId);
      if (result.success) {
        // Перезагружаем данные после успешного сохранения
        // для синхронизации с сервером
        await loadFinancialPlanData(projectId);

        // Показываем успешный попап
        setShowSuccessModal(true);

        // Автоматически скрываем через 3 секунды
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Ошибка сохранения:", error);
    }
  };

  // const handlePrintPDF = () => {
  //   // TODO: Реализовать печать PDF
  //   console.log("Печать PDF");
  // };

  // Обработчики для добавления исполнителя
  const handleAddSpecialist = (blockName, sectionName) => {
    setSelectedBlock(blockName);
    setSelectedSection(sectionName);
    setShowSelectSpecialistModal(true);
  };

  const handleCloseSelectSpecialistModal = () => {
    setShowSelectSpecialistModal(false);
    setSelectedBlock(null);
    setSelectedSection(null);
  };

  const handleSpecialistSubmit = (specialistData) => {
    // Сохраняем выбранного исполнителя (поддерживаем несколько исполнителей на блок)
    if (selectedBlock && selectedSection) {
      addSpecialist(selectedSection, selectedBlock, specialistData);
    }

    handleCloseSelectSpecialistModal();
  };

  // const handleSpecialistDelete = (blockName, sectionName, specialistIndex) => {
  //   removeSpecialist(sectionName, blockName, specialistIndex);
  // };

  // Функция для рендеринга активной вкладки
  const renderActiveTab = () => {
    switch (activeSubTab) {
      case "working-design":
        return (
          <WorkTab
            projectId={projectId}
            onAddSpecialist={handleAddSpecialist}
            AddUserIcon={AddUser}
            isEditable={isEditable}
          />
        );
      case "bim-coordination":
        return (
          <BimTab
            projectId={projectId}
            onAddSpecialist={handleAddSpecialist}
            AddUserIcon={AddUser}
            isEditable={isEditable}
          />
        );
      case "quality-control":
        return (
          <QualityTab
            projectId={projectId}
            onAddSpecialist={handleAddSpecialist}
            AddUserIcon={AddUser}
            isEditable={isEditable}
          />
        );
      case "project-delivery":
        return (
          <LeadTab
            projectId={projectId}
            onAddSpecialist={handleAddSpecialist}
            AddUserIcon={AddUser}
            isEditable={isEditable}
          />
        );
      case "project-management":
        return (
          <ProjectTab
            projectId={projectId}
            onAddSpecialist={handleAddSpecialist}
            AddUserIcon={AddUser}
            isEditable={isEditable}
          />
        );
      case "contract-sections":
        return <ContractSectionsTab isEditable={isEditable} />;
      default:
        return (
          <WorkTab
            onAddSpecialist={handleAddSpecialist}
            AddUserIcon={AddUser}
            isEditable={isEditable}
          />
        );
    }
  };

  // Показываем загрузку только при первоначальной загрузке данных
  // Не показываем лоадер во время калькуляции, чтобы не размонтировать компоненты
  const isInitialLoading = isLoading && !financialPlanData.rawApiData;

  if (isInitialLoading) {
    return <Preloader />;
  }

  // Показываем ошибку
  if (error) {
    return (
      <div className="error-message">
        <h3>Ошибка загрузки данных</h3>
        <p>{error}</p>
        <button onClick={() => loadFinancialPlanData(projectId)}>
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <div className="financial-plan">
      {/* Заголовок и кнопка истории версий */}
      <div className="financial-plan-top-bar">
        <div className="financial-plan-title">Финансовый план</div>
        <div className="financial-plan-top-bar-right">
          {showCancelButton && activeApproval && (
            <>
              {activeApproval.steps && activeApproval.steps.length > 0 && (
                <div className="financial-plan-approval-status-block">
                  {activeApproval.steps.map((step, index) => (
                    <div
                      key={step.id || index}
                      className="financial-plan-approval-step"
                    >
                      <span className="financial-plan-approver-name">
                        {step.approver_name || "Не указан"}:
                      </span>
                      {getStatusIcon(step.status)}
                      <span className="financial-plan-step-status-text">
                        {getStatusText(step.status)}
                      </span>
                      {step.comment && (
                        <span className="financial-plan-step-comment">
                          ({step.comment})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button
                className="financial-plan-cancel-approval-btn"
                onClick={handleCancelApproval}
                disabled={isCancellingApproval}
              >
                {isCancellingApproval ? "Отзыв..." : "Отозвать отправку"}
              </button>
            </>
          )}
          <div className="financial-plan-version-badge-container">
            {financialPlanData?.rawApiData?.version !== undefined &&
              financialPlanData?.rawApiData?.version !== null && (
                <span className="financial-plan-version-badge">
                  Версия {financialPlanData.rawApiData.version}
                </span>
              )}
            <button
              className="financial-plan-history-btn"
              onClick={() => setIsVersionHistoryOpen(true)}
            >
              <span className="financial-plan-history-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              История версий
              <span className="financial-plan-history-arrow">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Предупреждение о том, что финансовый план на согласовании */}
      {!isEditable && (
        <div
          style={{
            background: "#fff8f1",
            border: "1px solid #ffe0b2",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span style={{ fontSize: "24px" }}>⚠️</span>
          <span style={{ color: "#b26a00", fontSize: "14px" }}>
            Финансовый план находится на этапе согласования, поэтому
            редактирование заблокировано
          </span>
        </div>
      )}

      {/* План рентабельности проекта — по макету */}
      <div className="financial-plan-profitability-outer financial-plan-profitability-fullwidth">
        <div className="financial-plan-profitability-block">
          <h2 className="financial-plan-profitability-title">
            План рентабельности проекта
          </h2>

          <div className="financial-plan-profitability-grid">
            <div className="financial-plan-profitability-card financial-plan-card-income">
              <div className="financial-plan-profitability-icon financial-plan-icon-green">
                <img src={Coinssvg} alt="Доход" />
              </div>
              <div className="financial-plan-profitability-content">
                <div className="financial-plan-profitability-label">Доход</div>
                <div className="financial-plan-profitability-value">
                  <span className="currency">₸</span>{" "}
                  {financialPlanData.headerData?.contract_value?.toLocaleString() ||
                    "0"}
                </div>
                <div className="financial-plan-profitability-percent">
                  {financialPlanData.headerData?.total_sum_percent ?? "100"}
                  %
                </div>
              </div>
            </div>

            <div className="financial-plan-profitability-card financial-plan-card-kpn">
              <div className="financial-plan-profitability-icon financial-plan-icon-purple">
                <img src={House} alt="КПН" />
              </div>
              <div className="financial-plan-profitability-content">
                <div className="financial-plan-profitability-label">КПН</div>
                <div className="financial-plan-profitability-value">
                  <span className="currency">₸</span>{" "}
                  {financialPlanData.headerData?.nds_value?.toLocaleString() ||
                    "0"}
                </div>
                <div className="financial-plan-profitability-percent">8,0%</div>
              </div>
            </div>

            <div className="financial-plan-profitability-card financial-plan-card-fot">
              <div className="financial-plan-profitability-icon financial-plan-icon-yellow">
                <img src={People} alt="Налоги с ФОТ" />
              </div>
              <div className="financial-plan-profitability-content">
                <div className="financial-plan-profitability-label">
                  Налоги с ФОТ
                </div>
                <div className="financial-plan-profitability-value">
                  <span className="currency">₸</span>{" "}
                  {financialPlanData.headerData?.indirect_expenditures?.toLocaleString() ||
                    "0"}
                </div>
                <div className="financial-plan-profitability-percent">
                  {financialPlanData.headerData?.indirect_expenditures_percent ??
                    "0"}
                  %
                </div>
              </div>
            </div>

            <div className="financial-plan-profitability-card financial-plan-card-print">
              <div className="financial-plan-profitability-icon financial-plan-icon-gray">
                <img src={Print} alt="Распечатка" />
              </div>
              <div className="financial-plan-profitability-content">
                <div className="financial-plan-profitability-label">
                  Распечатка
                </div>
                <div className="financial-plan-profitability-value">
                  <span className="currency">₸</span>{" "}
                  {(
                    (financialPlanData.headerData?.contract_value || 0) * 0.01
                  ).toLocaleString()}
                </div>
                <div className="financial-plan-profitability-percent">1,0%</div>
              </div>
            </div>

            <div className="financial-plan-profitability-card financial-plan-card-unforeseen">
              <div className="financial-plan-profitability-icon financial-plan-icon-red">
                <img src={Sun} alt="Непредвиденные расходы" />
              </div>
              <div className="financial-plan-profitability-content">
                <div className="financial-plan-profitability-label">
                  Непредвиденные расходы
                </div>
                <div className="financial-plan-profitability-value">
                  <span className="currency">₸</span>{" "}
                  {(
                    (financialPlanData.headerData?.contract_value || 0) * 0.05
                  ).toLocaleString()}
                </div>
                <div className="financial-plan-profitability-percent">5,0%</div>
              </div>
            </div>

            <div className="financial-plan-profitability-card financial-plan-card-net-profit net-profit">
              <div className="financial-plan-profitability-icon financial-plan-icon-green">
                <img src={Sigma} alt="Чистая прибыль" />
              </div>
              <div className="financial-plan-profitability-content">
                <div className="financial-plan-profitability-label">
                  Чистая прибыль
                </div>
                <div className="financial-plan-profitability-value">
                  <span className="currency">₸</span>{" "}
                  {financialPlanData.headerData?.pure_income?.toLocaleString() ||
                    "0"}
                </div>
                <div className="financial-plan-profitability-percent financial-plan-profitability-percent-green">
                  {financialPlanData.headerData?.pure_income_percent ?? "0"}
                  %
                </div>
              </div>
            </div>
          </div>

          <div className="financial-plan-contract-sums">
            <div className="financial-plan-contract-sum-item">
              <div className="financial-plan-contract-sum-label">
                СУММА ОСНОВНОГО ДОГОВОРА
              </div>
              <div className="financial-plan-contract-sum-value">
                <span className="currency">₸</span>{" "}
                {financialPlanData.headerData?.contract_value?.toLocaleString() ||
                  "0"}
              </div>
            </div>
            <div className="financial-plan-contract-sum-item">
              <div className="financial-plan-contract-sum-label">
                СУММА ЗА ВЫЧЕТОМ НДС 12%
              </div>
              <div className="financial-plan-contract-sum-value">
                <span className="currency">₸</span>{" "}
                {financialPlanData.headerData?.contract_value_before_nds?.toLocaleString() ||
                  "0"}
              </div>
              <div className="financial-plan-contract-sum-nds">
                Сумма НДС <span className="currency">₸</span>{" "}
                {financialPlanData.headerData?.nds_value?.toLocaleString() ||
                  "0"}
              </div>
            </div>
          </div>

          <div className="financial-plan-list-items financial-plan-detail-list">
            <div className="financial-plan-list-item blue">
              <div className="financial-plan-list-dot blue"></div>
              <div className="financial-plan-list-content">
                <div className="financial-plan-list-label">
                  На разработку{" "}
                  {financialPlanData.headerData
                    ?.contract_value_construction_percent ?? "0"}
                  %
                </div>
                <div className="financial-plan-list-value">
                  <span className="currency">₸</span>{" "}
                  {financialPlanData.headerData?.contract_value_construction?.toLocaleString() ||
                    "0"}
                </div>
              </div>
            </div>
            <div className="financial-plan-list-item green">
              <div className="financial-plan-list-dot green"></div>
              <div className="financial-plan-list-content">
                <div className="financial-plan-list-label">Себестоимость</div>
                <div className="financial-plan-list-value">
                  <span className="currency">₸</span>{" "}
                  {financialPlanData.headerData?.cost_price?.toLocaleString() ||
                    "0"}
                </div>
              </div>
            </div>
            <div
              className={`financial-plan-list-item ${
                (financialPlanData.headerData?.difference ?? 0) >= 0
                  ? "green"
                  : "red"
              }`}
            >
              <div
                className={`financial-plan-list-dot ${
                  (financialPlanData.headerData?.difference ?? 0) >= 0
                    ? "green"
                    : "red"
                }`}
              />
              <div className="financial-plan-list-content">
                <div className="financial-plan-list-label">Отклонения</div>
                <div
                  className={`financial-plan-list-value ${
                    (financialPlanData.headerData?.difference ?? 0) < 0
                      ? "financial-plan-value-negative"
                      : ""
                  }`}
                >
                  <span className="currency">₸</span>{" "}
                  {(financialPlanData.headerData?.difference ?? 0).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Нижняя навигация */}
      <div className="financial-plan-table-block">
        <div className="financial-plan-bottom-tabs">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              className={`financial-plan-bottom-tab-btn ${
                activeSubTab === tab.id ? "active" : ""
              }`}
              onClick={() => setActiveSubTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Контент активной вкладки */}
        <div className="financial-plan-tab-content">{renderActiveTab()}</div>

        {/* Кнопки действий */}
        <div className="financial-plan-actions">
          <button
            className="btn-send-approval"
            onClick={() => setShowApprovalModal(true)}
            disabled={!isEditable}
            style={{
              opacity: !isEditable ? 0.6 : 1,
              cursor: !isEditable ? "not-allowed" : "pointer",
            }}
          >
            Отправить на согласование
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={!isEditable}
            style={{
              opacity: !isEditable ? 0.6 : 1,
              cursor: !isEditable ? "not-allowed" : "pointer",
            }}
          >
            Сохранить
          </button>
          {/* <button className="btn-print-pdf" onClick={handlePrintPDF}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M14 2H2V14H14V2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6 6H10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6 9H10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6 12H8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Печать PDF
          </button> */}
        </div>
      </div>

      {/* История версий */}
      <VersionHistoryDrawer
        open={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        projectId={projectId}
        onVersionRestored={() => {
          loadFinancialPlanData(projectId);
          getFinancialPlanApprovals(projectId).then((approvals) =>
            setFinancialPlanApprovals(approvals || [])
          );
          getFinancialPlanApprovalStatuses(parseInt(projectId)).then(
            (statuses) => setApprovalStatuses(statuses || [])
          );
        }}
      />

      {/* Модальное окно согласования */}
      <ApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        projectData={projectData}
        isSubmitting={isSubmittingApproval}
      />

      {/* Модальное окно выбора исполнителя */}
      <SelectSpecialistModal
        open={showSelectSpecialistModal}
        onClose={handleCloseSelectSpecialistModal}
        onSubmit={handleSpecialistSubmit}
        blockName={selectedBlock}
        sectionName={selectedSection}
      />

      {/* Модальное окно успешного сохранения */}
      {showSuccessModal && (
        <>
          <div
            className="approval-modal-backdrop"
            onClick={() => setShowSuccessModal(false)}
          />
          <div className="approval-modal">
            <div className="approval-modal-title">Успешно!</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#f0f8f4",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#226a3a",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>✅</span>
              <span>
                {successMessage || "Финансовый план успешно сохранен"}
              </span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn green"
                onClick={() => setShowSuccessModal(false)}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      )}

      {/* Модальное окно ошибки */}
      {showErrorModal && (
        <>
          <div
            className="approval-modal-backdrop"
            onClick={() => setShowErrorModal(false)}
          />
          <div className="approval-modal">
            <div className="approval-modal-title">Ошибка</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#fff8f1",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#b26a00",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>⚠️</span>
              <span>{errorMessage}</span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn outline"
                onClick={() => setShowErrorModal(false)}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Обертка с провайдером контекста
const FinancialPlan = ({ projectId }) => {
  return (
    <FinancialPlanStateProvider>
      <FinancialPlanComponent projectId={projectId} />
    </FinancialPlanStateProvider>
  );
};

export default FinancialPlan;
